import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, X } from 'lucide-react';
import { supabase } from '../supabase';
import { colors as theme, fonts } from '../theme';
import ConfirmDialog from '../ui/ConfirmDialog';
import { toastError, toastSuccess } from '../ui/toast';
import { pounds, when } from '../services/coachPlanService';
import {
  loadOwnerCoachBilling, makePayingCoach, markFlagDone, setPaymentsSwitch, setOwnerCoach, flagWords, flagDetail, planWords,
} from '../services/ownerCoachBillingService';

// The owner's coach billing screen (plan 014; migration 112): the switch and what it needs, every coach's plan,
// making a coach a paying coach, and the flags. Owner only: the database refuses anyone else. Desktop-first, as
// every coach screen (owner, 21 October 2026).

const card = {
  backgroundColor: theme.surface1, border: `1px solid ${theme.surface3}`, borderRadius: '14px', padding: '20px', marginBottom: '16px',
};
const label = { color: theme.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' };
const button = (kind = 'primary') => ({
  padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  border: kind === 'primary' ? 'none' : `1px solid ${kind === 'danger' ? theme.danger : theme.borderStrong}`,
  backgroundColor: kind === 'primary' ? theme.gold : 'transparent',
  color: kind === 'primary' ? theme.bg : kind === 'danger' ? theme.danger : theme.text,
});
const cell = { padding: '10px 8px', borderTop: `1px solid ${theme.surface3}`, fontSize: '13px', color: theme.text, verticalAlign: 'top' };
const head = { ...label, padding: '0 8px 8px', textAlign: 'left' };

function CheckRow({ ok, children, unknown = false }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0', fontSize: '14px', color: theme.text }}>
      <span aria-label={unknown ? 'not checked here' : ok ? 'done' : 'not done'} style={{ color: unknown ? theme.textMuted : ok ? theme.success : theme.danger, flexShrink: 0 }}>
        {unknown ? '?' : ok ? <Check size={16} /> : <X size={16} />}
      </span>
      <span>{children}</span>
    </div>
  );
}

// The scheduler's own record of a coach job (113): a stopped scheduler cannot warn about itself
const JOB_NAMES = { 'coach-billing-jobs': 'The billing jobs', 'coach-cover-tick': 'The cover tick' };
export function schedulerWords(j) {
  const name = JOB_NAMES[j.name] || j.name;
  const every = j.every_minutes === 1 ? 'every minute' : `every ${j.every_minutes} minutes`;
  if (!j.scheduled) return `${name} (${every}): not scheduled`;
  if (!j.active) return `${name} (${every}): switched off in the scheduler`;
  // No error text is sent (Codex #311): the engineering assistant reads it in the scheduler itself
  if (j.last_status === 'failed') {
    return `${name} (${every}): its latest attempt failed${j.last_run_at ? `; last ran ${when(j.last_run_at)}` : ''}`;
  }
  if (!j.last_run_at) return `${name} (${every}): never run`;
  if (j.running_since && j.late) return `${name} (${every}): still running since ${when(j.running_since)}, which is too long`;
  return `${name} (${every}): last ran ${when(j.last_run_at)}${j.late ? ', which is late' : ''}`;
}

// What the next date on a plan means
function dateWords(p) {
  if (p.status === 'ended') return `Ended ${when(p.ended_at, false)}`;
  if (p.access_ended) return `Ended ${when(p.plan_end, false)} (being recorded)`;
  if (p.deadline) return `Must pay by ${when(p.deadline)}`;
  if (p.band === 'founding') return p.founding_until ? `Founding until ${when(p.founding_until, false)}` : '';
  if (p.state === 'cancelling') return `Ends ${when(p.paid_through, false)}`;
  if (p.paid_through && (p.state === 'paid' || p.state === 'stopping')) return `${p.state === 'paid' ? 'Renews' : 'Stops'} ${when(p.paid_through, false)}`;
  // A custom or older plan ends at its paid-through date (093), as coach_plan_end says (PR 307)
  if (p.band !== 'standard' && p.plan_end) return `Ends ${when(p.plan_end, false)}`;
  return '';
}

export const REFRESH_MS = 120000;

const OwnerCoachBilling = ({ onBack, onFlagsChange }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);   // { kind: 'switch'|'make'|'flag', ... }
  const [showDone, setShowDone] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(false);
  // When the screen was last read, and a refresh that failed: health is a snapshot (Codex #311)
  const [checkedAt, setCheckedAt] = useState(null);
  const [refreshFailedAt, setRefreshFailedAt] = useState(null);
  // Kept in a ref, so a new function from the parent on each render does not reload the page
  const flagsChanged = useRef(onFlagsChange);
  flagsChanged.current = onFlagsChange;

  const load = useCallback(async () => {
    try {
      const out = await loadOwnerCoachBilling({ oldestFirst });
      setData(out);
      setFailed(false);
      setCheckedAt(new Date().toISOString());
      setRefreshFailedAt(null);
      flagsChanged.current?.(out.open_flags_total);
    } catch (err) {
      console.error('[owner-coach-billing] could not load:', err.message);
      setFailed(true);
      setRefreshFailedAt(new Date().toISOString());
    }
  }, [oldestFirst]);
  useEffect(() => { load(); }, [load]);
  // Read again every 2 minutes while the screen is in view, so an open screen does not stay green after a stop
  useEffect(() => {
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (key, work) => {
    setBusy(key);
    try { await work(); } catch (err) { toastError(err.message); } finally { setBusy(null); await load(); }
  };

  const setSwitch = (on) => act('switch', async () => {
    const out = await setPaymentsSwitch(on);
    if (out.result === 'billing_live') {
      toastError(`Not switched off: ${out.coaches} ${out.coaches === 1 ? 'coach is' : 'coaches are'} being billed. Ask your engineering assistant.`);
      return;
    }
    toastSuccess(on ? 'Coach payments are switched on' : 'Coach payments are switched off');
  });
  const useMyAccount = () => act('owner', async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Please sign in again.');
    await setOwnerCoach(user.id);
    toastSuccess('Your account is set as the coach for clients who move to you');
  });
  const makePaying = (c) => act(c.coach_id, async () => {
    const out = await makePayingCoach(c.coach_id, c.clients_linked > 0);
    if (out?.result === 'made') toastSuccess(`${c.name} is now a paying coach. They add a card on their Plan & Code page.`);
    else if (out?.result === 'already_has_a_plan') toastError(`${c.name} already has a plan.`);
    else if (out?.result === 'has_clients') toastError(`${c.name} has clients now, so this needs the exception. Look again and confirm.`);
    else toastError(`${c.name} is not an active coach.`);
  });
  const done = (f) => act(`flag-${f.id}`, async () => {
    const ok = await markFlagDone(f.id);
    if (!ok) toastError('That flag had already been marked done.');
  });

  const page = (body) => (
    <div style={{ padding: '24px 20px', backgroundColor: theme.bg, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <button type="button" onClick={onBack} style={{ ...button('quiet'), display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
          <ArrowLeft size={16} /> Back
        </button>
        <h1 style={{ color: theme.text, fontSize: '28px', fontWeight: 700, margin: '0 0 6px' }}>Coach billing</h1>
        {body}
      </div>
    </div>
  );

  if (failed && !data) {
    return page(
      <div role="alert" style={{ ...card, borderColor: theme.danger, color: theme.text }}>
        Coach billing could not be loaded. Nothing has changed.{' '}
        <button type="button" style={button('quiet')} onClick={load}>Try again</button>
      </div>,
    );
  }
  if (!data) return page(<p style={{ color: theme.textMuted }}>Loading…</p>);

  const c = data.checks;
  const jobsOk = !!c.jobs_last_answered_at && !c.jobs_unanswered_since;
  const ready = c.owner_coach_set && c.stripe_prices_set && c.vault_secrets === 2;
  // Off is not a stop button: Stripe carries on (PR 307, P1)
  const offBlocked = data.features_on && data.billing_live > 0;
  const lateJobs = (c.scheduler || []).filter((j) => j.late);
  const open = data.flags.filter((f) => !f.done_at);
  const finished = data.flags.filter((f) => f.done_at);

  return page(
    <>
      <p style={{ color: theme.textMuted, fontSize: '14px', margin: '0 0 24px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>Paying coaches, the switch, and everything the system has flagged for you.</span>
        <span style={{ color: theme.textSubtle }}>Checked {checkedAt ? when(checkedAt) : ''}; reads again every 2 minutes</span>
        <button type="button" style={{ ...button('quiet'), padding: '4px 10px' }} disabled={!!busy} onClick={load}>Refresh</button>
      </p>
      {refreshFailedAt && (
        <div role="status" style={{ ...card, color: theme.text, fontSize: '14px' }}>
          Could not read again at {when(refreshFailedAt)}. What you see was read at {when(checkedAt)}.
        </div>
      )}

      {lateJobs.length > 0 && (
        <div role="alert" style={{ ...card, borderColor: theme.danger, backgroundColor: theme.dangerDim, color: theme.text, fontSize: '14px', lineHeight: 1.5 }}>
          <strong>The coach jobs are not running as expected.</strong> {lateJobs.map((j) => JOB_NAMES[j.name] || j.name).join(' and ').replace(/ and The /, ' and the ')}
          {lateJobs.length === 1 ? ' is' : ' are'} late, failing or switched off (see below), and the scheduler cannot flag this itself.
          Until {lateJobs.length === 1 ? 'it runs' : 'they run'} again, deadlines, hand-overs and billing records are delayed;
          Stripe keeps charging renewals as normal. Ask your engineering assistant.
        </div>
      )}

      {/* The switch, and what the database can see of what it needs */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={label}>Coach payments</div>
            <div style={{ color: data.features_on ? theme.success : theme.text, fontSize: '22px', fontWeight: 700, marginTop: '4px' }}>
              {data.features_on ? 'Switched on' : 'Switched off'}
            </div>
          </div>
          <button type="button" style={button(data.features_on ? 'danger' : 'primary')} disabled={!!busy || offBlocked}
            onClick={() => setConfirm({ kind: 'switch', on: !data.features_on })}>
            {data.features_on ? 'Switch off' : 'Switch on'}
          </button>
        </div>
        {offBlocked && (
          <div role="note" style={{ color: theme.text, fontSize: '13px', lineHeight: 1.5, marginBottom: '12px', padding: '10px 12px',
            border: `1px solid ${theme.borderStrong}`, borderRadius: '8px' }}>
            {data.billing_live} {data.billing_live === 1 ? 'coach is' : 'coaches are'} being billed, so this switch cannot be turned off here.
            Turning it off does not stop Stripe: subscriptions keep renewing, while the repair jobs and coaches' own card, stop and stay
            buttons stop working. To stop billing, ask your engineering assistant.
          </div>
        )}
        <CheckRow ok={c.owner_coach_set}>
          Your coach account, for clients who move to you: {data.owner_coach ? data.owner_coach.name : 'not set'}
          {!c.owner_coach_set && (
            <button type="button" style={{ ...button('quiet'), marginLeft: '10px', padding: '4px 10px' }} disabled={!!busy} onClick={useMyAccount}>
              Use my account
            </button>
          )}
        </CheckRow>
        <CheckRow ok={c.stripe_prices_set}>Stripe price ids entered for price version {data.price.version} ({pounds(data.price.base_pence)} a month, {pounds(data.price.extra_pence)} per extra client); not checked against Stripe</CheckRow>
        <CheckRow ok={c.vault_secrets === 2}>The jobs' address and secret have entries in Supabase's vault ({c.vault_secrets} of 2); their values are not checked</CheckRow>
        <CheckRow ok={jobsOk}>
          The jobs have answered{c.jobs_last_answered_at ? `: last ${when(c.jobs_last_answered_at)}` : ': not yet (they run only once switched on)'}
          {c.jobs_unanswered_since ? `; unanswered since ${when(c.jobs_unanswered_since)}` : ''}
        </CheckRow>
        {c.scheduler
          ? c.scheduler.map((j) => <CheckRow key={j.name} ok={!j.late}>{schedulerWords(j)}</CheckRow>)
          : <CheckRow unknown>The scheduler: cannot be checked here</CheckRow>}
        <CheckRow unknown>Vercel's COACH_JOBS_SECRET: cannot be checked from here</CheckRow>
        <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '8px', lineHeight: 1.5 }}>
          These show what the database can see. A full set of ticks does not prove that charging, renewals or cancelling work:
          try one coach end to end in Stripe's test mode first.
        </div>
      </div>

      {/* Flags */}
      <div style={card}>
        <div style={{ ...label, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>
            Needs you · {data.open_flags_total}
            {data.open_flags_total > open.length ? ` (the ${oldestFirst ? 'oldest' : 'newest'} ${open.length} are shown)` : ''}
          </span>
          {(data.open_flags_total > open.length || oldestFirst) && (
            <button type="button" style={{ ...button('quiet'), padding: '4px 10px', textTransform: 'none', letterSpacing: 0 }}
              onClick={() => setOldestFirst((v) => !v)}>
              {oldestFirst ? 'Show newest first' : 'Show oldest first'}
            </button>
          )}
        </div>
        {open.length === 0 && <div style={{ color: theme.textMuted, fontSize: '14px' }}>Nothing needs you.</div>}
        {open.map((f) => (
          <FlagRow key={f.id} f={f} busy={busy} onDone={() => (flagWords(f).tone === 'act' ? setConfirm({ kind: 'flag', flag: f }) : done(f))} />
        ))}
        {finished.length > 0 && (
          <button type="button" style={{ ...button('quiet'), marginTop: '12px' }} onClick={() => setShowDone((v) => !v)}>
            {showDone ? 'Hide recently done' : `Recently done (${finished.length})`}
          </button>
        )}
        {showDone && finished.map((f) => <FlagRow key={f.id} f={f} />)}
      </div>

      {/* Coaches with a plan */}
      <div style={card}>
        <div style={{ ...label, marginBottom: '12px' }}>Coaches on a plan · {data.plans.length}</div>
        {data.plans.length === 0 ? (
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>No coach is on a plan yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={head}>Coach</th><th style={head}>Code</th><th style={head}>Plan</th><th style={head}>Covered</th>
              <th style={head}>Waiting</th><th style={head}>Next</th><th style={head}>Flags</th>
            </tr></thead>
            <tbody>
              {data.plans.map((p) => (
                <tr key={p.coach_id} data-testid="coach-plan-row">
                  <td style={cell}>
                    {p.name}
                    <div style={{ color: theme.textMuted, fontSize: '12px' }}>{p.email}</div>
                    {p.account_status !== 'active' && <div style={{ color: theme.danger, fontSize: '12px' }}>Account {p.account_status}</div>}
                  </td>
                  <td style={{ ...cell, fontFamily: fonts.mono }}>{p.code || '-'}</td>
                  <td style={cell}>{p.band === 'standard' ? 'Paying' : planWords(p)}<div style={{ color: theme.textMuted, fontSize: '12px' }}>{p.band === 'standard' ? planWords(p) : ''}</div></td>
                  <td style={cell}>
                    {p.held} of {p.client_limit}
                    <div style={{ color: theme.textMuted, fontSize: '12px' }}>
                      {p.band === 'standard' ? `highest ${p.highest} · ` : ''}{p.clients_linked} linked
                    </div>
                  </td>
                  <td style={cell}>{p.waiting}</td>
                  <td style={{ ...cell, color: p.deadline || p.access_ended ? theme.danger : theme.text }}>{dateWords(p)}</td>
                  <td style={cell}>{p.open_flags || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Coaches with no plan */}
      <div style={card}>
        <div style={{ ...label, marginBottom: '4px' }}>Coaches with no plan · {data.coaches_without_plan.length}</div>
        <div style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '12px' }}>
          A paying coach pays {pounds(data.price.base_pence)} a month for up to {data.price.included} covered clients and {pounds(data.price.extra_pence)} for each extra one.
          Nothing is charged until they add a card and accept their first covered client. A coach who already has clients is
          changing model, which is your exception to make.
        </div>
        {data.coaches_without_plan.map((co) => (
          <div key={co.coach_id} data-testid="coach-without-plan" style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0', borderTop: `1px solid ${theme.surface3}`, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px', fontSize: '14px', color: theme.text }}>
              {co.name}<span style={{ color: theme.textMuted }}> · {co.email}{co.code ? ` · ${co.code}` : ''}</span>
              <div style={{ color: theme.textMuted, fontSize: '12px' }}>{co.clients_linked} {co.clients_linked === 1 ? 'client' : 'clients'} today</div>
            </div>
            <button type="button" style={button('quiet')} disabled={!!busy} onClick={() => setConfirm({ kind: 'make', coach: co })}>
              {busy === co.coach_id ? 'Working…' : co.clients_linked > 0 ? 'Make paying coach (exception)' : 'Make paying coach'}
            </button>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={confirm?.kind === 'switch'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const on = confirm.on; setConfirm(null); setSwitch(on); }}
        title={confirm?.on ? 'Switch coach payments on?' : 'Switch coach payments off?'}
        message={confirm?.on
          ? `Coaches' codes start taking requests, Accept and first charges work, and the billing jobs start. Founding coaches' cover follows the same switch.${ready ? '' : ' Not everything above is ticked: anything missing will fail and flag you.'} Try one coach end to end in Stripe's test mode before switching on for real.`
          : 'Codes stop taking requests, and Accepts, first charges and the billing jobs stop. No coach is being billed, so nothing is left running in Stripe. Founding coaches\' cover carries on to its normal end.'}
        confirmText={confirm?.on ? 'Switch on' : 'Switch off'}
        cancelText="Cancel"
        variant={confirm?.on && !ready ? 'danger' : 'warning'}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'make'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const co = confirm.coach; setConfirm(null); makePaying(co); }}
        title={confirm?.kind === 'make' ? `Make ${confirm.coach.name} a paying coach?` : ''}
        message={confirm?.kind !== 'make' ? '' : confirm.coach.clients_linked > 0
          ? `${confirm.coach.name} already has ${confirm.coach.clients_linked} ${confirm.coach.clients_linked === 1 ? 'client' : 'clients'}, so this changes their model: plan 014 keeps that as your exception. Their clients keep their access and any subscription of their own; nobody is moved onto ${confirm.coach.name}'s bill. From now on new clients wait for ${confirm.coach.name}'s Accept, and if the plan ever ends, clients with no covered place move to you. Nothing is charged until they add a card and accept a first covered client. This cannot be undone from this screen.`
          : `They will see a Plan & Code page asking for a card. Nothing is charged until they add one and accept their first covered client. This cannot be undone from this screen.`}
        confirmText={confirm?.kind === 'make' && confirm.coach.clients_linked > 0 ? 'Make the exception' : 'Make paying coach'}
        cancelText="Cancel"
        variant="warning"
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'flag'}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const f = confirm.flag; setConfirm(null); done(f); }}
        title="Mark this done?"
        message={confirm?.kind === 'flag' && confirm.flag.kind === 'plan_ended_handover_held'
          ? 'Do this once you have fixed the reason. The next run then tries again, and this comes back if the reason remains.'
          : 'Only once it has been dealt with. Marking it done fixes nothing in Stripe or the app, and the same problem will not be flagged again.'}
        confirmText="Mark done"
        cancelText="Keep it"
        variant="warning"
      />
    </>,
  );
};

function FlagRow({ f, busy, onDone }) {
  const w = flagWords(f);
  const who = [f.coach_name && `Coach: ${f.coach_name}`, f.client_name && `Client: ${f.client_name}`].filter(Boolean).join(' · ');
  const extra = flagDetail(f.detail);
  return (
    <div data-testid="owner-flag" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '12px 0', borderTop: `1px solid ${theme.surface3}`, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 400px' }}>
        <div style={{ color: w.tone === 'act' && !f.done_at ? theme.gold : theme.text, fontWeight: 600, fontSize: '14px' }}>{w.title}</div>
        {w.text && <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '2px' }}>{w.text}</div>}
        <div style={{ color: theme.textSubtle, fontSize: '12px', marginTop: '4px' }}>
          {when(f.created_at)}{who ? ` · ${who}` : ''}{extra.length ? ` · ${extra.join(' · ')}` : ''}{f.done_at ? ` · done ${when(f.done_at)}` : ''}
        </div>
      </div>
      {!f.done_at && onDone && (
        <button type="button" style={button('quiet')} disabled={!!busy} onClick={onDone}>
          {busy === `flag-${f.id}` ? 'Working…' : 'Mark done'}
        </button>
      )}
    </div>
  );
}

export default OwnerCoachBilling;
